// A SABLON ELŐÁLLÍTÁSA — két munkalapos XLSX, ÉLŐ adatból, VALÓDI legördülőkkel.
//
// MIÉRT NEM STATIKUS MINTA-FÁJL: a kitöltőnek kategória-UTAT kell megadnia (`Advisory bér / Bónusz`), mert a
// puszta levél-név nem azonosít — élesben 78 levélre csak 45 egyedi név jut. Ha gépelni kell, el is gépelik:
// a fában MA is két írásmód él egymás mellett, egyetlen ékezet különbséggel.
//
// MIÉRT LEGÖRDÜLŐ, ÉS MIÉRT NEM ELÉG EGY „választható értékek" LAP (user-lelet): a kitöltő nem fog
// átmásolgatni — összevissza ír, és a hibát csak az előnézet mondja meg, sok sorral később. A legördülő a
// KITÖLTÉS pillanatában vezet; a szabad-szöveges oszlopok pedig súgó-buborékot kapnak, formátum-példával.
//
// A `write-excel-file`-nak nincs validáció-API-ja, de VAN bővítmény-pontja (`Feature`): a worksheet XML-jébe
// beszúrható egy `<dataValidations>` blokk (a könyvtár a sibling-sorrendet ismeri). Így valódi Excel-dropdown
// lesz belőle, ÚJ FÜGGŐSÉG NÉLKÜL.

import { IMPORT_COLUMNS, type ImportColumn } from './file';
import { download } from '@/lib/export';

import type { ExpenseImportTemplate } from '@atlas/contracts';

/** Egy cella a `write-excel-file` 2D-mátrix alakjában. */
interface Cell {
  value?: string;
  fontWeight?: 'bold';
  wrap?: boolean;
}

// A fejléc FÉLKÖVÉR, háttérszín NÉLKÜL: a hex itt stílus-döntés lenne, arra viszont a rendszernek
// design-tokenje van — egy táblázat-cellába pedig token nem fér bele. A félkövér elég a fejléc jelöléséhez.
const head = (value: string): Cell => ({ value, fontWeight: 'bold' });

/** Meddig érvényes a legördülő és a súgó. Bőven a sor-plafon (500) fölött, hogy ne fogyjon el a vezetés. */
const VALIDATED_ROWS = 600;

/** `0` → `A`, `25` → `Z`, `26` → `AA`. A sablon 14 oszlopos, de a képlet legyen általános. */
export function columnLetter(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

const escapeXml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Egy oszlop vezetése: LISTA a második lapról, vagy csak súgó-buborék. */
export interface Guide {
  column: ImportColumn;
  title: string;
  prompt: string;
  /** A második lap oszlop-betűje és sor-darabszáma — ha van, a cella LEGÖRDÜLŐT kap. */
  list?: { letter: string; count: number };
}

/**
 * A `dataValidations` XML.
 *
 * A LISTA a második lap egy TARTOMÁNYÁRA hivatkozik, nem beégetett érték-sorra: így a 78 kategória-út és az
 * 56 név is belefér — a beégetett lista 255 karakternél elvágódna, és a felsorolás NÉMÁN csonkulna.
 *
 * `showErrorMessage`: a listán kívüli értéket az Excel ELUTASÍTJA. Szándékos szigor — a szerver úgyis
 * visszadobná, csak sokkal később, a kitöltés végén.
 */
export function dataValidationsXml(guides: readonly Guide[], valuesSheet: string): string {
  // A lapnév aposztrófok közt megy a képletbe (szóközt tartalmazhat); a benne lévő aposztróf duplázódik.
  const sheetRef = `'${valuesSheet.replace(/'/g, "''")}'`;
  const items = guides.map((g) => {
    const col = columnLetter(IMPORT_COLUMNS.indexOf(g.column));
    const sqref = `${col}2:${col}${VALIDATED_ROWS}`;
    const common =
      `allowBlank="1" showInputMessage="1" promptTitle="${escapeXml(g.title)}" ` +
      `prompt="${escapeXml(g.prompt)}" sqref="${sqref}"`;
    if (!g.list) return `<dataValidation type="none" ${common}/>`;
    const range = `${sheetRef}!$${g.list.letter}$2:$${g.list.letter}$${g.list.count + 1}`;
    return (
      `<dataValidation type="list" showErrorMessage="1" errorStyle="stop" ` +
      `errorTitle="${escapeXml(g.title)}" error="${escapeXml(g.prompt)}" ${common}>` +
      `<formula1>${escapeXml(range)}</formula1></dataValidation>`
    );
  });
  return `<dataValidations count="${items.length}">${items.join('')}</dataValidations>`;
}

/**
 * A `<dataValidations>` blokk beszúrása a HELYES pozícióba. Az OOXML elem-sorrendje KÖTÖTT: a blokk a
 * `sheetData` UTÁN és a `pageMargins` ELŐTT áll — rossz helyre téve az Excel érvénytelennek mondja a fájlt.
 */
export function insertDataValidations(sheetXml: string, block: string): string {
  const at = sheetXml.indexOf('<pageMargins');
  if (at >= 0) return `${sheetXml.slice(0, at)}${block}${sheetXml.slice(at)}`;
  const afterData = sheetXml.indexOf('</sheetData>');
  if (afterData >= 0) {
    const end = afterData + '</sheetData>'.length;
    return `${sheetXml.slice(0, end)}${block}${sheetXml.slice(end)}`;
  }
  return sheetXml;
}

export interface TemplateTexts {
  labels: Record<ImportColumn, string>;
  sheets: { rows: string; values: string };
  valueHeaders: {
    categories: string;
    salary: string;
    employees: string;
    issuers: string;
    currencies: string;
    noBill: string;
    months: string;
  };
  /** A „nincs számla" legördülő két szava a hívó nyelvén — az oszlop azt kérdezi, VAN-e bizonylat. */
  noBillWords: { missing: string; present: string };
  /** Oszloponkénti súgó: cím + útmutató (formátum-példával) — ez jelenik meg a cellára kattintva. */
  guides: Record<ImportColumn, { title: string; prompt: string }>;
}

/** A legördülők és a súgók összeállítása — a lista-oszlopok a második lap A–G hasábjaira mutatnak. */
export function buildGuides(
  data: ExpenseImportTemplate,
  texts: TemplateTexts,
  months: readonly string[],
): Guide[] {
  const guide = (column: ImportColumn, list?: Guide['list']): Guide => ({
    column,
    ...texts.guides[column],
    ...(list ? { list } : {}),
  });
  return [
    guide('issuerKey', { letter: 'D', count: data.issuerKeys.length }),
    guide('categoryPath', { letter: 'A', count: data.categoryPaths.length }),
    guide('employeeName', { letter: 'C', count: data.employeeNames.length }),
    guide('currency', { letter: 'E', count: data.currencies.length }),
    // NINCS SZÁMLA: igen/nem — ugyanaz a szándék-nyilatkozat, mint az űrlap kapcsolója.
    guide('noBill', { letter: 'F', count: 2 }),
    // VONATKOZÁSI HÓNAP: ugyanaz az ablak, mint a lap sor-cellájában (6 hónap vissza, 3 előre).
    guide('accountingMonth', { letter: 'G', count: months.length }),
    // A többi oszlop szabad szöveg — ott a súgó-buborék vezet.
    guide('supplierName'),
    guide('supplierTaxNumber'),
    guide('invoiceNumber'),
    guide('issueDate'),
    guide('fulfillmentDate'),
    guide('dueDate'),
    guide('netAmount'),
    guide('vatAmount'),
    guide('note'),
  ];
}

/**
 * A sablon letöltése. A FEJLÉC a magyar felirat (azt látja a kitöltő), a beolvasó viszont a technikai
 * kulcsot is elfogadja — így egy kézzel átírt fejléc sem töri el a visszatöltést.
 */
export async function downloadImportTemplate(
  data: ExpenseImportTemplate,
  texts: TemplateTexts,
  /** A felkínált vonatkozási hónapok — a lap sor-cellájával AZONOS ablak (`monthOptions`). */
  months: readonly string[],
): Promise<void> {
  // A `browser` alpont, ahogy a lista-export is hívja: a csomag gyökerén nincs típus-belépő.
  const { default: writeExcelFile } = await import('write-excel-file/browser');

  // 1. MUNKALAP: a kitöltendő tábla — csak a fejléc.
  const sheet1: Cell[][] = [IMPORT_COLUMNS.map((c) => head(texts.labels[c]))];

  // 2. MUNKALAP: a választható értékek. Minden hasáb a SAJÁT oszlopában, hogy a legördülő tartománya stabil
  // legyen (A = kategória · B = ebből bér · C = munkatárs · D = kibocsátó · E = deviza · F = igen/nem ·
  // G = vonatkozási hónap).
  const columns = [
    { title: texts.valueHeaders.categories, values: data.categoryPaths },
    { title: texts.valueHeaders.salary, values: data.salaryCategoryPaths },
    { title: texts.valueHeaders.employees, values: data.employeeNames },
    { title: texts.valueHeaders.issuers, values: [...data.issuerKeys] },
    { title: texts.valueHeaders.currencies, values: [...data.currencies] },
    {
      title: texts.valueHeaders.noBill,
      values: [texts.noBillWords.missing, texts.noBillWords.present],
    },
    { title: texts.valueHeaders.months, values: [...months] },
  ];
  const depth = Math.max(0, ...columns.map((c) => c.values.length));
  const sheet2: Cell[][] = [columns.map((c) => head(c.title))];
  for (let i = 0; i < depth; i++) {
    sheet2.push(columns.map((c) => ({ value: c.values[i] ?? '' })));
  }

  const xml = dataValidationsXml(buildGuides(data, texts, months), texts.sheets.values);
  const validationFeature = {
    files: {
      // A bővítmény-alak KÖTÖTT: `files.transform[<fájl>].transform(xml, sheetOptions, { sheetIndex, sheetId })`.
      // (Van `insert` is, de az a gyökér-elem VÉGÉRE fűz — a `<dataValidations>` ott már a `pageMargins`
      // MÖGÉ kerülne, és az OOXML elem-sorrend kötött, tehát a fájl érvénytelen lenne.)
      //
      // ⚠️ A `sheetIndex` a 0-alapú sorszám; a `sheetId` SZÖVEG (`"1"`), nem szám — a `=== 1` összevetés
      // némán hamis volt, és a validáció-blokk nyomtalanul kimaradt a fájlból (mérve).
      transform: {
        'xl/worksheets/sheet{id}.xml': {
          transform: (
            sheetXml: string,
            _sheetOptions: unknown,
            properties: { sheetIndex?: number },
          ): string =>
            // CSAK az ELSŐ munkalapra (a kitöltendő tábla); a második a választható értékeké.
            properties.sheetIndex === 0 ? insertDataValidations(sheetXml, xml) : sheetXml,
        },
      },
    },
  };

  const blob = await writeExcelFile(
    [
      {
        // ⚠️ A lap NEVE a `sheet` kulcs (NEM `name`) — másként a lapok `Sheet1`/`Sheet2` néven jönnek, és a
        // legördülő tartomány-hivatkozása nem találná meg a második lapot (mérve: a `name` némán kimaradt).
        sheet: texts.sheets.rows,
        data: sheet1,
        columns: IMPORT_COLUMNS.map(() => ({ width: 22 })),
      },
      { sheet: texts.sheets.values, data: sheet2, columns: columns.map(() => ({ width: 38 })) },
    ] as never,
    { features: [validationFeature] } as never,
  ).toBlob();
  download(blob, 'atlas-kiadas-import-sablon.xlsx');
}
